import { coerceCalendarDate } from './calendarDateKey';
import { resolveTimelineArtifactType } from '../features/artifacts/artifactTypes';

export const formatLocalDateInput = (dateValue) => {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
    return '';
  }

  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const day = String(dateValue.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const buildUtcTimestampFromLocalInput = (dateValue, timeValue) => {
  const timestamp = new Date(dateValue);
  if (timeValue) {
    const [hours, minutes] = timeValue.split(':').map((value) => Number(value));
    if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
      timestamp.setHours(hours, minutes, 0, 0);
    }
  }
  return timestamp;
};

export const getTimezoneMetadata = () => {
  const timeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  const timeZoneOffsetMinutes = -new Date().getTimezoneOffset();
  return { timeZoneName, timeZoneOffsetMinutes };
};

export const buildCanonicalQuickNoteSavePayload = ({
  child,
  text = '',
  selectedDate = new Date(),
  selectedTime = '',
  resolvedCategory = 'log',
  importantMoment = false,
  user = null,
  noteData = {},
  tags = [],
  parserMode = 'quick_log',
  sourceMode = 'quick_log',
  inputMode = 'quick_log',
  metadata = {},
} = {}) => {
  const canonicalText = String(text || noteData?.text || noteData?.note || noteData?.body || '').trim();
  const title = String(noteData?.title || canonicalText || 'Quick note').trim();
  const { timeZoneName, timeZoneOffsetMinutes } = getTimezoneMetadata();
  const artifactType = resolveTimelineArtifactType({
    category: resolvedCategory,
    logCategory: resolvedCategory,
    type: noteData?.type,
    candidateType: noteData?.candidateType,
    metadata,
  });

  return {
    childId: child?.id || null,
    text: canonicalText || 'Shared media note',
    body: canonicalText || 'Shared media note',
    note: canonicalText || 'Shared media note',
    content: canonicalText || 'Shared media note',
    description: canonicalText || 'Shared media note',
    summary: canonicalText || 'Shared media note',
    details: canonicalText || 'Shared media note',
    value: canonicalText || 'Shared media note',
    title,
    titlePrefix: String(noteData?.titlePrefix || noteData?.label || resolvedCategory || 'Note').trim(),
    status: resolvedCategory === 'sleep' ? 'completed' : 'active',
    category: resolvedCategory,
    artifactType,
    tags,
    importantMoment,
    timestamp: buildUtcTimestampFromLocalInput(selectedDate, selectedTime),
    timestampUtc: buildUtcTimestampFromLocalInput(selectedDate, selectedTime),
    localDate: formatLocalDateInput(selectedDate),
    localTime: selectedTime || null,
    timeSource: selectedTime ? 'explicit' : 'missing',
    timeBucket: '',
    timeInferred: false,
    timeInferenceReason: '',
    timeZoneName,
    timeZoneOffsetMinutes,
    timestampSource: 'local-input',
    source: {
      type: parserMode,
      mode: inputMode,
    },
    authorId: user?.uid,
    authorName: user?.displayName || user?.email?.split('@')[0] || 'User',
    authorEmail: user?.email,
    sourceText: canonicalText || 'Shared media note',
    originalText: canonicalText || 'Shared media note',
    rawText: canonicalText || 'Shared media note',
    rawSourceText: canonicalText || 'Shared media note',
    parserMode,
    sourceMode,
    inputMode,
    canonicalRowVersion: 1,
    metadata: {
      ...(metadata || {}),
      parserMode,
      sourceMode,
      inputMode,
      canonicalRowVersion: 1,
    },
  };
};

export const buildCanonicalStructuredQuickNoteSaveEntry = ({
  entity = {},
  child = null,
  noteText = '',
  selectedDate = new Date(),
  selectedTime = '',
  importantMoment = false,
  user = null,
  isBackendSmartCapture = false,
  allowSelectedTimeForSingleEntity = false,
  index = 0,
  timeZoneName = null,
  timeZoneOffsetMinutes = 0,
  combinedTags = [],
} = {}) => {
  const entityTimestamp = coerceCalendarDate(entity?.timestamp || entity?.metadata?.capturedAt || null);
  const entityTime = entity.time
    || (entity?.timeInferred && entityTimestamp ? entityTimestamp.toTimeString().slice(0, 5) : null)
    || (allowSelectedTimeForSingleEntity && !isBackendSmartCapture && index === 0 ? selectedTime : null)
    || null;
  const timeBucket = entity?.timeBucket || entity?.metadata?.timeBucket || null;
  const timestampUtc = entityTime
    ? buildUtcTimestampFromLocalInput(selectedDate, entityTime)
    : buildUtcTimestampFromLocalInput(selectedDate, '00:00');
  const entryText = String(
    entity.body
    || entity.text
    || entity.note
    || entity.description
    || entity.summary
    || entity.details
    || entity.value
    || entity.content
    || entity.sourceText
    || entity.originalText
    || entity.title
    || noteText.trim()
  ).trim();
  const entryTitle = String(entity.title || entryText).trim();
  const entryTitlePrefix = String(entity.titlePrefix || entity.label || entity.category || '').trim();
  const artifactType = resolveTimelineArtifactType(entity);

  return {
    childId: child?.id || null,
    text: entryText,
    body: entryText,
    note: entryText,
    title: entryTitle,
    titlePrefix: entryTitlePrefix,
    content: entryText,
    description: entryText,
    summary: entryText,
    details: entryText,
    value: entryText,
    status: entity.category === 'sleep' ? 'completed' : 'active',
    category: entity.category,
    artifactType,
    tags: combinedTags,
    importantMoment,
    timestamp: timestampUtc,
    timestampUtc,
    localDate: formatLocalDateInput(selectedDate),
    localTime: entityTime,
    timeSource: entity?.timeSource || (entity?.timeInferred ? 'inferred' : (entityTime ? 'explicit' : 'missing')),
    timeBucket,
    timeInferred: Boolean(entity?.timeInferred),
    timeInferenceReason: entity?.timeInferenceReason || entity?.metadata?.timeInferenceReason || timeBucket || '',
    timeZoneName,
    timeZoneOffsetMinutes,
    timestampSource: 'quick-note',
    source: {
      type: entity?.source?.type || entity?.parserMode || 'quick_log',
      mode: entity?.source?.mode || entity?.sourceMode || 'quick_log',
    },
    authorId: user?.uid,
    authorName: user?.displayName || user?.email?.split('@')[0] || 'User',
    authorEmail: user?.email,
    sourceText: String(entity.sourceText || entity.originalText || noteText || entryText || '').trim(),
    originalText: String(entity.originalText || entity.sourceText || noteText || entryText || '').trim(),
    rawText: String(entity.rawText || noteText || entryText || '').trim(),
    rawSourceText: String(entity.rawSourceText || noteText || entryText || '').trim(),
    parserMode: entity?.parserMode || 'quick_log',
    sourceMode: entity?.sourceMode || 'quick_log',
    inputMode: entity?.inputMode || 'quick_log',
    canonicalRowVersion: entity?.canonicalRowVersion || 1,
    metadata: {
      ...(entity?.metadata || {}),
      parserMode: entity?.parserMode || 'quick_log',
      sourceMode: entity?.sourceMode || 'quick_log',
      inputMode: entity?.inputMode || 'quick_log',
      canonicalRowVersion: entity?.canonicalRowVersion || 1,
    },
  };
};
